import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import SessionTimeoutModal from './SessionTimeoutModal';
import { useAuthUser } from '@nxsys/hooks/AuthHooks';
import useIdleTimer from '@nxsys/hooks/useIdleTimer';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { endPoints } from '@nxsys/services/endpoints';
import jwtAxios from '@nxsys/services/axios';
import { AUTH_CODE, TOKEN } from '@nxsys/store/constants/keys';
import {
  TOKEN_RENEW_MIN_GAP_MS,
  renewBelowRemainingMs,
  resolveSessionPolicy,
} from '@nxsys/constants/session';
import type { RootState } from '@nxsys/store';
import {
  markSessionExpired,
  registerSessionExpiryHandler,
  resetSessionExpiryLatch,
  signInUrlWithReturn,
} from '@nxsys/services/session/sessionExpiry';

/**
 * True while the New UI is running on a hand-off auth_code instead of its own
 * token — the route the classic UI uses to drop a user into a New UI screen.
 *
 * In that mode ApiConfig sends `?auth_code=` and no Bearer, and the backend's
 * AuthMiddleware mints a throwaway token per request, so there is no session for
 * a renewal to extend: it would refresh against whatever token the previous user
 * left in storage and write the result over it. The idle timer itself still runs
 * and still signs the user out; only the server-side half of the policy is
 * missing for this cohort, which is recorded on NX-5238.
 *
 * Clearing this key writes `""` rather than removing it, hence the parse.
 */
function isAuthCodeSession(): boolean {
  try {
    const raw = localStorage.getItem(AUTH_CODE);
    return Boolean(raw && JSON.parse(raw));
  } catch {
    return false;
  }
}

/**
 * Milliseconds left on the stored token, or 0 when there is nothing readable.
 * Returning 0 for an unreadable token biases towards renewing, which costs one
 * request; guessing the other way would risk letting a session lapse unwarned.
 */
function msRemainingOnToken(): number {
  try {
    const token = localStorage.getItem(TOKEN);
    if (!token) return 0;
    const [, payload] = token.split('.');
    const { exp } = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    );
    return typeof exp === 'number' ? exp * 1000 - Date.now() : 0;
  } catch {
    return 0;
  }
}

/**
 * Signs an idle user out of the New UI and warns them a minute first (NX-5238).
 *
 * Two clocks have to agree for this to behave. This component owns the one the
 * user sees; the backend owns the one that actually rejects the token. They are
 * kept together by renewing the token whenever the user is active, so an active
 * user's server-side deadline never arrives — which is the part the ticket cares
 * about most, since today's fixed hour signs people out mid-task.
 */
const SessionTimeout = () => {
  const { isAuthenticated } = useAuthUser();
  // The server owns the window; /app-configs carries it, and appSlice persists
  // it to localStorage, so a reload has last-known values before the fetch
  // returns. The built-in defaults only cover a browser that has never loaded
  // the app.
  const servedSession = useSelector(
    (state: RootState) => state.appStore.appConfigs?.session,
  );
  const policy = useMemo(
    () => resolveSessionPolicy(servedSession),
    [servedSession],
  );
  const dispatch = useDispatch<any>();
  const navigate = useNavigateWithQuery();
  const { messages } = useIntl();

  const lastRefreshAt = useRef(Date.now());
  const refreshInFlight = useRef(false);

  const signOut = useCallback(
    (expired: boolean) => {
      // Read before the sign-out is under way, while the address bar still holds
      // the screen being left.
      const signInUrl = signInUrlWithReturn();
      // logout.fulfilled clears localStorage and empties the auth slice.
      Promise.resolve(dispatch(appActions.logout())).finally(() => {
        // After the teardown, not before: logout.fulfilled clears the reason so
        // that an ordinary sign-out cannot leave one behind, and doing this
        // first would hand that teardown our own reason to delete (NX-5238).
        if (expired) markSessionExpired(policy.idleMs);
        resetSessionExpiryLatch();
        // The login URL already carries the screen to come back to, query
        // string and all, so the query of the page being left must not be
        // preserved on top of it.
        navigate(signInUrl, { preserveQuery: false, replace: true });
      });
    },
    [dispatch, navigate, policy.idleMs],
  );

  /**
   * Slides the server-side deadline forward for a user who is demonstrably
   * still working, but only once the token can no longer cover a full fresh
   * idle window. Reading the deadline off the token rather than tracking
   * elapsed time is what guarantees the client always reaches its own deadline
   * — and therefore shows the warning — before the server refuses the token.
   */
  const renewToken = useCallback(async () => {
    if (isAuthCodeSession()) return;
    if (refreshInFlight.current) return;
    if (Date.now() - lastRefreshAt.current < TOKEN_RENEW_MIN_GAP_MS) return;
    if (msRemainingOnToken() > renewBelowRemainingMs(policy)) return;

    refreshInFlight.current = true;
    try {
      const { data } = await jwtAxios.post(endPoints.refreshSession);
      if (data?.token) {
        // Stores the token and only the token. Rebuilding the rest of the auth
        // state from localStorage on a background timer would revert anything
        // the store holds that has not been written back to it.
        dispatch(appActions.setToken(data.token));
      }
    } catch {
      // A failed renewal is not itself a reason to sign out: the current token
      // is still valid until its own deadline, the next activity retries, and a
      // genuinely dead session surfaces as a 401 on the next real request.
    } finally {
      // Stamped on the attempt, not on success. Advancing it only when a renewal
      // worked meant the 30s gate could never hold again after the first
      // failure: activity is throttled to one write a second, so every tick
      // fired another POST /auth/refresh, each one a 401 and a Sentry event, for
      // as long as the session lasted (Victor, review of #1753). `refreshInFlight`
      // stops two from overlapping, not from repeating.
      lastRefreshAt.current = Date.now();
      refreshInFlight.current = false;
    }
  }, [dispatch, policy]);

  const { isWarning, secondsRemaining, reset } = useIdleTimer({
    enabled: isAuthenticated,
    policy,
    onIdle: () => signOut(true),
    onActivity: renewToken,
  });

  // The axios interceptor cannot dispatch or navigate on its own, so it calls
  // whatever is registered here when the server rejects a token mid-request.
  useEffect(() => {
    if (!isAuthenticated) return;
    // A token the server has stopped accepting is an idle expiry too, so this
    // sign-out is announced like the timer's own.
    return registerSessionExpiryHandler(() => signOut(true));
  }, [isAuthenticated, signOut]);

  /**
   * Brings the two clocks together at the start of a session (NX-5238).
   *
   * A token starts ageing when it is minted, which is when the credentials are
   * accepted — but a user with an account in both systems is then shown the
   * picker, and nothing here is running while they read it: the idle timer is
   * disabled until they choose, so no activity is recorded and no renewal can
   * fire. That wait comes off the server's deadline only. Choose late enough
   * and the session arrives already unable to cover the window the timer is
   * about to start, so the server refuses the token before the warning is due
   * and the user is dropped with no warning at all — the one outcome the ticket
   * rules out.
   *
   * Waiting for the first activity is not enough, because there may not be any:
   * the renewal below is deliberately not gated on the user doing something.
   */
  const hasRealignedSession = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      hasRealignedSession.current = false;
      lastRefreshAt.current = Date.now();
      return;
    }
    if (hasRealignedSession.current) return;
    hasRealignedSession.current = true;
    // Nothing has been renewed on this session yet, so the floor that stops a
    // burst of activity stacking requests must not hold this one back.
    lastRefreshAt.current = 0;
    renewToken();
  }, [isAuthenticated, renewToken]);

  if (!isAuthenticated || !isWarning) return null;

  return (
    <SessionTimeoutModal
      open
      secondsRemaining={secondsRemaining}
      totalSeconds={Math.round(policy.warningMs / 1000)}
      countdownLabel={
        (messages['session.timeout.countdownLabel'] as string) ??
        'Signing out in'
      }
      unitsLabel={
        (messages['session.timeout.units'] as string) ?? 'minutes : seconds'
      }
      title={
        (messages['session.timeout.title'] as string) ??
        'Your session is about to end'
      }
      description={
        (messages['session.timeout.description'] as string) ??
        'For your security we sign you out after a period of inactivity. Any unsaved payroll changes will be lost.'
      }
      stayLabel={
        (messages['session.timeout.stay'] as string) ?? 'Keep me signed in'
      }
      // Dismissing the warning — button, mask, or Escape — is itself activity
      // and reopens the full window, per the ticket.
      onStay={reset}
    />
  );
};

export default SessionTimeout;
